import React, { useState } from "react";
import { api, formatApiError, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldAlert, Wrench, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const BillAuditDialog = ({ onFixed }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fixing, setFixing] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/audit/overpaid-bills");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const openAudit = () => { setOpen(true); run(); };

  const fix = async (it) => {
    if (!window.confirm(`Perbaiki tagihan "${it.label}" (${it.student_name})?\nDibayar ${rupiah(it.paid_amount)} → ${rupiah(it.total_amount)}, hapus ${it.duplicate_tx_ids.length} entri duplikat.`)) return;
    setFixing(it.bill_id);
    try {
      const r = await api.post(`/admin/audit/overpaid-bills/${it.bill_id}/fix`);
      toast.success(`Diperbaiki. ${r.data.deleted_tx} entri duplikat dihapus.`);
      await run();
      onFixed?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFixing(null);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={openAudit} data-testid="audit-bills-button" className="rounded-xl">
        <ShieldAlert size={16} className="mr-1.5" /> Audit Pembayaran
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Audit Kelebihan Bayar</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">Tagihan yang nominal dibayarnya melebihi total tagihan (indikasi transaksi ganda).</p>
          {loading ? (
            <div className="py-8 text-center text-stone-500"><Loader2 className="animate-spin mx-auto mb-2" /> Memeriksa…</div>
          ) : data && data.count === 0 ? (
            <div className="py-8 text-center text-emerald-700" data-testid="audit-clean">
              <CheckCircle2 className="mx-auto mb-2" /> Tidak ada tagihan kelebihan bayar.
            </div>
          ) : data ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="text-sm font-medium text-rose-700" data-testid="audit-summary">
                {data.count} tagihan bermasalah · total kelebihan {rupiah(data.total_excess)}
              </div>
              {data.items.map((it) => (
                <div key={it.bill_id} className="rounded-xl border p-3 flex items-start justify-between gap-3" data-testid={`audit-item-${it.bill_id}`}>
                  <div className="text-sm">
                    <p className="font-semibold text-stone-900">{it.student_name} <span className="text-stone-400 font-mono text-xs">{it.nisn}</span></p>
                    <p className="text-stone-600">{it.label}</p>
                    <p className="mt-1 font-mono text-xs">
                      Total {rupiah(it.total_amount)} · Dibayar <b className="text-rose-700">{rupiah(it.paid_amount)}</b> · {it.ledger_entries} entri ledger, {it.duplicate_tx_ids.length} duplikat
                    </p>
                  </div>
                  <Button size="sm" disabled={fixing === it.bill_id} onClick={() => fix(it)} data-testid={`fix-bill-${it.bill_id}`} className="bg-rose-600 hover:bg-rose-700 rounded-lg shrink-0">
                    <Wrench size={14} className="mr-1" /> Perbaiki
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
